import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, User, Lightbulb } from "lucide-react";
import { KNOWLEDGE } from "../data/business/knowledge";
import { Seo, breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../components/Seo";
import BusinessHeader from "../components/business/BusinessHeader";
import BusinessFooter from "../components/business/BusinessFooter";
import { FaqBlock } from "../components/business/blocks";
import NotFoundPage from "./NotFoundPage";
import { DbArticle } from "../components/viden/DbArticle";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8B7500";
const NAVY = "#0B1220";

const Aurora = () => (
  <div className="votra-aurora-wrap" aria-hidden>
    <div className="absolute inset-0 votra-gridlines opacity-60" />
    <div className="votra-blob votra-blob--gold" />
    <div className="votra-blob votra-blob--cyan" />
    <div className="votra-blob votra-blob--gold2" />
  </div>
);

const Block = ({ b, answer }) => {
  if (b.type === "lead") return <p className="text-xl md:text-2xl leading-relaxed text-slate-700 mb-8 font-light">{b.text}</p>;
  if (b.type === "answer") {
    if (!answer) return null;
    return (
      <div className="my-10 rounded-2xl border p-6 md:p-7" style={{ borderColor: "rgba(212,175,55,0.45)", backgroundColor: "rgba(212,175,55,0.06)" }} data-testid="article-answer">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD_DARK }}>
          <Lightbulb className="h-4 w-4" /> Kort svar
        </p>
        <p className="text-slate-800 text-lg leading-relaxed">{answer}</p>
      </div>
    );
  }
  if (b.type === "h") return <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-12 mb-4">{b.text}</h2>;
  if (b.type === "quote") return (
    <blockquote className="my-10 pl-6 border-l-2 font-display italic text-xl md:text-2xl text-slate-800 leading-relaxed" style={{ borderColor: GOLD }}>
      {b.text}
    </blockquote>
  );
  if (b.type === "list") return (
    <ul className="my-6 space-y-3">
      {b.items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-slate-700 text-lg leading-relaxed">
          <span className="mt-2.5 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  return <p className="text-lg leading-relaxed text-slate-700 mb-5">{b.text}</p>;
};

const PerspektivArticle = () => {
  const { slug } = useParams();
  const article = KNOWLEDGE[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return <DbArticle company="business" slug={slug} notFound={<NotFoundPage />} />;

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return d; }
  };

  const path = `/business/viden/${article.slug}`;
  const related = (article.related || []).map((s) => KNOWLEDGE[s]).filter(Boolean);

  return (
    <div className="votra-biz min-h-screen bg-white" data-testid="perspektiv-article">
      <Seo
        title={article.metaTitle || `${article.title} | Votra Business`}
        description={article.metaDescription || article.excerpt}
        path={path}
        image={article.image}
        brand="business"
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Forside", path: "/" },
            { name: "Votra Business", path: "/business" },
            { name: "Viden", path: "/business/viden" },
            { name: article.title, path },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${SITE_URL}${path}#article`,
            headline: article.title,
            description: article.excerpt,
            articleSection: article.track || article.tag,
            image: article.image,
            datePublished: article.date,
            dateModified: article.date,
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: "Votra Business ApS" },
            mainEntityOfPage: `${SITE_URL}${path}`,
          },
          ...(article.faq?.length ? [faqJsonLd(article.faq, `${SITE_URL}${path}#faq`)] : []),
        ]}
      />
      <div className="votra-grain" aria-hidden />
      <BusinessHeader />

      {/* Hero */}
      <main id="indhold">
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <div className="absolute inset-0" style={{ backgroundImage: `url(${article.image})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.28 }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(11,18,32,0.7) 0%, rgba(11,18,32,0.92) 100%)` }} />
        <Aurora />
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
        <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="votra-hero-rise votra-hero-rise--1">
            <Link to="/business/viden"
              className="inline-block text-xs font-semibold uppercase tracking-[0.28em] mb-5 hover:underline" style={{ color: GOLD }}
              data-testid="article-back-btn">
              Viden · {article.track || article.tag}
            </Link>
            <h1 className="font-display text-white font-medium leading-[1.1] text-3xl md:text-5xl max-w-3xl mb-6">{article.title}</h1>
            <p className="text-slate-300 text-lg max-w-2xl mb-8">{article.excerpt}</p>
            <div className="flex flex-wrap items-center gap-5 text-sm text-slate-400">
              <span className="inline-flex items-center gap-2"><User className="h-4 w-4" style={{ color: GOLD }} /> {article.author}</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: GOLD }} /> {article.readingTime}</span>
              <span>{fmtDate(article.date)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="max-w-[760px] mx-auto px-4 sm:px-6 py-14 md:py-20">
        {article.body.map((b, i) => <Block key={i} b={b} answer={article.answer} />)}

        <div className="mt-14 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <p><span className="font-semibold text-slate-700">Forfatter & afsender:</span> {article.author}. Udgivet {fmtDate(article.date)}.</p>
          <p className="mt-1">Indholdet er skrevet til vejledende brug og udgør ikke juridisk eller økonomisk rådgivning. Tal i regneeksempler er opdigtede og alene til at vise metoden.</p>
        </div>

        <div className="mt-12 rounded-2xl p-8 text-center" style={{ backgroundColor: NAVY }}>
          <h3 className="font-display text-2xl text-white mb-3">Skal vi tage kontoret?</h3>
          <p className="text-slate-300 mb-6">Få en driftsgennemgang: hvad vi overtager, hvad det kræver af dig, og hvad det koster.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/business/kontakt" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold" style={{ backgroundColor: GOLD, color: NAVY }} data-testid="article-cta-btn">
              Få en driftsgennemgang <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/business/beregnere" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors" data-testid="article-calc-btn">
              Åbn beregnerne
            </Link>
          </div>
        </div>
      </article>

      {article.faq?.length > 0 && <FaqBlock items={article.faq} heading="Spørgsmål om emnet" tone="slate" />}

      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-white" data-testid="article-related">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] mb-8" style={{ color: GOLD_DARK }}>Læs også</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Link key={r.slug} to={`/business/viden/${r.slug}`}
                  className="group block text-left rounded-2xl border border-slate-200 p-6 hover:shadow-xl transition-all duration-300"
                  data-testid={`related-article-${i}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD_DARK }}>{r.track || r.tag}</p>
                  <h3 className="font-display text-lg font-medium text-slate-900 leading-snug mb-2">{r.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold mt-4" style={{ color: GOLD_DARK }}>
                    Læs <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      </main>
      <BusinessFooter />
    </div>
  );
};

export default PerspektivArticle;
